import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, TrendingUp, TrendingDown, Wallet, Loader2, RefreshCw, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtSigned = (n) => {
    const v = Number(n) || 0;
    const abs = Math.abs(v);
    const sign = v >= 0 ? '+' : '-';
    return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// UI preset per category — key must match backend _CATEGORY_MAP values.
const CATEGORY_STYLE = {
    loads:            { emoji: '↓', tone: 'emerald', desc: 'PayPal receipts, cashier loads, admin deposits' },
    promos:           { emoji: '★', tone: 'amber',  desc: 'Daily bonuses, freeplay, gifts received' },
    casino_play:      { emoji: '♠', tone: 'violet', desc: 'Credits ↔ Majik casino balance. Net negative = casino house edge (normal).' },
    lottery:          { emoji: '🎟', tone: 'sky',    desc: 'Lottery clawbacks from draw corrections + void refunds' },
    cashout_playback: { emoji: '↩', tone: 'sky',    desc: 'Aborted cashouts returned to credits' },
    peer_transfers:   { emoji: '⇄', tone: 'zinc',   desc: 'Player-to-player transfers and gifts' },
    cashouts:         { emoji: '↑', tone: 'rose',   desc: 'PayPal payouts, cashier cash-outs, approved cashout requests' },
    jackpots:         { emoji: '★', tone: 'amber',  desc: 'Jackpot payouts to players' },
    lucky_pack:       { emoji: '🎁', tone: 'violet', desc: 'Lucky Pack entries, payouts, refunds' },
    adjustments:      { emoji: '✎', tone: 'zinc',   desc: 'Admin manual credit adjustments / loan repayments' },
    wagers:           { emoji: '⚔', tone: 'sky',    desc: 'Sportsbook wagers settled' },
    other:            { emoji: '?', tone: 'zinc',   desc: 'Transactions with an unrecognized type' },
};

const TONE_CLASSES = {
    emerald: 'border-emerald-500/40 bg-emerald-500/5 text-emerald-100',
    amber:   'border-amber-500/40 bg-amber-500/5 text-amber-100',
    violet:  'border-violet-500/40 bg-violet-500/5 text-violet-100',
    sky:     'border-sky-500/40 bg-sky-500/5 text-sky-100',
    zinc:    'border-zinc-500/40 bg-zinc-500/5 text-zinc-100',
    rose:    'border-rose-500/40 bg-rose-500/5 text-rose-100',
};

// Simple preset window buttons — user's most common asks.
const WINDOW_PRESETS = [
    { key: 'today',   label: 'Today (from 7 AM CT)', hours: null /* server default: 7 AM Central business day */ },
    { key: 'shift',   label: 'Last 12h',  hours: 12 },
    { key: '24h',     label: 'Last 24h',  hours: 24 },
    { key: '7d',      label: 'Last 7 days', hours: 24 * 7 },
    { key: '30d',     label: 'Last 30 days', hours: 24 * 30 },
];

/**
 * Full-page modal showing where every credit went in the chosen window.
 * Categorises every credit-touching transaction into IN vs OUT columns,
 * with drill-down to raw transaction_type so nothing is hidden.
 *
 * Powered by GET /staff/credit-flow-ledger.
 */
const CreditFlowLedgerModal = ({
    open, onClose,
    whales = [], whaleThreshold = 500,
    cashoutWhales = [], cashoutWhaleThreshold = 200,
    onConfigureThreshold,
}) => {
    const [preset, setPreset] = useState('today');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());
    // Cached top-players lookup: `${category}|${direction}` → { loading, rows }
    const [topPlayersByCat, setTopPlayersByCat] = useState({});

    const fetchData = useCallback(async (presetKey) => {
        setLoading(true);
        setError(null);
        try {
            const p = WINDOW_PRESETS.find((x) => x.key === presetKey) || WINDOW_PRESETS[0];
            const params = {};
            if (p.hours !== null) {
                const since = new Date(Date.now() - p.hours * 3600 * 1000);
                params.since_iso = since.toISOString();
            } // else: server defaults to the 7 AM CT business day
            const { data } = await axios.get(`${API}/staff/credit-flow-ledger`, {
                params, withCredentials: true,
            });
            setData(data);
            // Reset drill-down cache — the window changed.
            setTopPlayersByCat({});
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Failed to load ledger');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) fetchData(preset);
    }, [open, preset, fetchData]);

    const fetchTopPlayers = useCallback(async (categoryKey) => {
        const cacheKey = `${categoryKey}|both`;
        setTopPlayersByCat((prev) => ({ ...prev, [cacheKey]: { loading: true, rows: prev[cacheKey]?.rows || [] } }));
        try {
            const p = WINDOW_PRESETS.find((x) => x.key === preset) || WINDOW_PRESETS[0];
            const params = { category: categoryKey, direction: 'both', limit: 8 };
            if (p.hours !== null) {
                params.since_iso = new Date(Date.now() - p.hours * 3600 * 1000).toISOString();
            }
            const { data } = await axios.get(`${API}/staff/credit-flow-ledger/top-players`, {
                params, withCredentials: true,
            });
            setTopPlayersByCat((prev) => ({ ...prev, [cacheKey]: { loading: false, rows: data?.rows || [] } }));
        } catch (e) {
            setTopPlayersByCat((prev) => ({ ...prev, [cacheKey]: { loading: false, rows: [], error: e.response?.data?.detail || e.message } }));
        }
    }, [preset]);

    const toggle = useCallback((key) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else {
                next.add(key);
                // Kick off lazy fetch on first expand
                const cacheKey = `${key}|both`;
                setTopPlayersByCat((cache) => {
                    if (!cache[cacheKey]) {
                        // fire-and-forget — mounted flag not needed since state
                        // updater checks the current cache on completion
                        fetchTopPlayers(key);
                        return { ...cache, [cacheKey]: { loading: true, rows: [] } };
                    }
                    return cache;
                });
            }
            return next;
        });
    }, [fetchTopPlayers]);

    if (!open) return null;

    const cats = data?.categories || [];
    const totalIn = data?.in?.total || 0;
    const totalOut = data?.out?.total || 0;
    const net = data?.net_flow || 0;
    const currentFloat = data?.current_float || 0;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
            onClick={onClose}
            data-testid="credit-flow-ledger-modal"
        >
            <div
                className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-4xl my-4 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-zinc-950 rounded-t-2xl">
                    <div>
                        <h2 className="text-lg font-black text-white flex items-center gap-2" data-testid="ledger-title">
                            <Wallet className="w-5 h-5 text-emerald-400" />
                            Credit Flow Ledger
                        </h2>
                        <p className="text-[11px] text-zinc-400 mt-0.5">
                            Where did the credits go? Every credit-touching transaction, grouped by category.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => fetchData(preset)}
                            disabled={loading}
                            className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white disabled:opacity-50"
                            title="Refresh"
                            data-testid="ledger-refresh"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white"
                            title="Close"
                            data-testid="ledger-close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Preset selector */}
                <div className="px-5 py-3 border-b border-white/5 flex flex-wrap gap-1.5">
                    {WINDOW_PRESETS.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => setPreset(p.key)}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                                preset === p.key
                                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200 font-black'
                                    : 'bg-zinc-900 border-white/10 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                            }`}
                            data-testid={`ledger-preset-${p.key}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Whale-alert banner — appears whenever any player crosses
                    a configured threshold today. Shows two independent
                    sections: casino net-losers and cashout whales. */}
                {(whales.length > 0 || cashoutWhales.length > 0) && (
                    <div className="px-5 py-3 border-b border-rose-500/40 bg-rose-500/10" data-testid="ledger-whale-banner">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0 space-y-3">
                                <div className="text-[13px] font-black text-rose-100" data-testid="ledger-whale-headline">
                                    {whales.length + cashoutWhales.length} whale{(whales.length + cashoutWhales.length) === 1 ? '' : 's'} today
                                    {' · '}
                                    <span className="opacity-80 font-normal text-[11px]">
                                        Consider reaching out with a small comp before they churn.
                                    </span>
                                </div>

                                {whales.length > 0 && (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-rose-300/80 font-bold mb-1">
                                            🎰 Casino losses ≥ {fmt(whaleThreshold)}
                                        </div>
                                        <div className="space-y-0.5">
                                            {whales.slice(0, 8).map((w) => (
                                                <div key={`c-${w.user_id}`} className="flex items-center justify-between text-[11px]" data-testid={`ledger-whale-row-${w.user_id}`}>
                                                    <span className="font-mono truncate">
                                                        {w.name}
                                                        {w.phone && <span className="opacity-40 ml-1">· {w.phone}</span>}
                                                    </span>
                                                    <span className="flex items-center gap-2 flex-shrink-0">
                                                        <span className="text-[9px] opacity-60 font-mono">{w.count}×</span>
                                                        <span className="font-mono font-black text-rose-200">-{fmt(w.net_loss)}</span>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {cashoutWhales.length > 0 && (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-rose-300/80 font-bold mb-1">
                                            💸 Cashouts today ≥ {fmt(cashoutWhaleThreshold)}
                                        </div>
                                        <div className="space-y-0.5">
                                            {cashoutWhales.slice(0, 8).map((w) => (
                                                <div key={`co-${w.user_id}`} className="flex items-center justify-between text-[11px]" data-testid={`ledger-cashout-whale-row-${w.user_id}`}>
                                                    <span className="font-mono truncate">
                                                        {w.name}
                                                        {w.phone && <span className="opacity-40 ml-1">· {w.phone}</span>}
                                                    </span>
                                                    <span className="flex items-center gap-2 flex-shrink-0">
                                                        <span className="text-[9px] opacity-60 font-mono">{w.count}×</span>
                                                        <span className="font-mono font-black text-rose-200">-{fmt(w.net_loss)}</span>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {onConfigureThreshold && (
                                <button
                                    type="button"
                                    onClick={onConfigureThreshold}
                                    className="text-[10px] text-rose-200/70 hover:text-white underline whitespace-nowrap"
                                    data-testid="ledger-whale-configure"
                                >
                                    change thresholds
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {/* Summary strip */}
                <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-white/5">
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-300/70 font-bold">Total IN</div>
                        <div className="text-lg font-mono font-black text-emerald-200 mt-0.5" data-testid="ledger-total-in">
                            +{fmt(totalIn)}
                        </div>
                        <div className="text-[10px] text-emerald-300/60">{data?.in?.count || 0} txns</div>
                    </div>
                    <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-rose-300/70 font-bold">Total OUT</div>
                        <div className="text-lg font-mono font-black text-rose-200 mt-0.5" data-testid="ledger-total-out">
                            -{fmt(totalOut)}
                        </div>
                        <div className="text-[10px] text-rose-300/60">{data?.out?.count || 0} txns</div>
                    </div>
                    <div className={`rounded-lg border p-3 ${net >= 0 ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
                        <div className={`text-[10px] uppercase tracking-widest font-bold ${net >= 0 ? 'text-emerald-300/70' : 'text-rose-300/70'}`}>Net flow</div>
                        <div className={`text-lg font-mono font-black mt-0.5 ${net >= 0 ? 'text-emerald-200' : 'text-rose-200'}`} data-testid="ledger-net-flow">
                            {fmtSigned(net)}
                        </div>
                        <div className="text-[10px] text-zinc-400">in - out</div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-zinc-900/60 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Current float</div>
                        <div className="text-lg font-mono font-black text-white mt-0.5" data-testid="ledger-current-float">
                            {fmt(currentFloat)}
                        </div>
                        <div className="text-[10px] text-zinc-500">live snapshot</div>
                    </div>
                </div>

                {/* Cash for this window — today's profit kept apart from older cash-outs paid (owner rule, Nov 2026) */}
                {data?.cash && (
                    <div className="px-5 py-3 border-b border-white/5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ledger-cash-strip">
                        <div className="rounded-lg border border-white/10 bg-zinc-900/60 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Cash-outs asked &amp; paid</div>
                            <div className="text-base font-mono font-black text-white mt-0.5" data-testid="ledger-cash-today">-{fmt(data.cash.cashouts_today)}</div>
                            <div className="text-[10px] text-zinc-500">loads {fmt(data.cash.loads_today)}</div>
                        </div>
                        <div className={`rounded-lg border p-3 ${data.cash.profit_today >= 0 ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
                            <div className="text-[10px] uppercase tracking-widest text-emerald-300/70 font-bold">Profit (this window)</div>
                            <div className={`text-base font-mono font-black mt-0.5 ${data.cash.profit_today >= 0 ? 'text-emerald-200' : 'text-rose-200'}`} data-testid="ledger-cash-profit">{fmtSigned(data.cash.profit_today)}</div>
                            <div className="text-[10px] text-zinc-500">loads − cash-outs asked &amp; paid</div>
                        </div>
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">Old cash-outs paid</div>
                            <div className="text-base font-mono font-black text-amber-200 mt-0.5" data-testid="ledger-cash-old">{fmt(data.cash.old_cashouts_paid)}</div>
                            <div className="text-[10px] text-amber-300/60">{data.cash.old_cashouts_count} asked before this window · not in profit</div>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-zinc-900/60 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Still owed</div>
                            <div className="text-base font-mono font-black text-white mt-0.5" data-testid="ledger-cash-owed">{fmt(data.cash.owed_today)}</div>
                            <div className="text-[10px] text-zinc-500">{data.cash.owed_today_count} asked this window · total sent {fmt(data.cash.cash_sent_today)}</div>
                        </div>
                        {data.old_cashouts_approved?.count > 0 && (
                            <p className="col-span-2 sm:col-span-4 text-[11px] text-amber-200/80" data-testid="ledger-old-approved">
                                {data.old_cashouts_approved.count} older cash-out{data.old_cashouts_approved.count === 1 ? '' : 's'} approved in this window ({fmt(data.old_cashouts_approved.amount)} credits) — kept out of the totals above.
                            </p>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className="p-5 space-y-2 max-h-[65vh] overflow-y-auto">
                    {loading && !data && (
                        <div className="flex items-center justify-center py-10 text-zinc-500 gap-2 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading ledger…
                        </div>
                    )}
                    {error && (
                        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-rose-200 text-sm flex items-start gap-2" data-testid="ledger-error">
                            <AlertTriangle className="w-4 h-4 mt-0.5" />
                            <div>
                                <div className="font-bold">Couldn&apos;t load ledger</div>
                                <div className="text-[12px] text-rose-200/70">{String(error)}</div>
                            </div>
                        </div>
                    )}
                    {!loading && !error && cats.length === 0 && (
                        <div className="text-zinc-500 text-center py-8 text-sm">
                            No credit-touching transactions in this window.
                        </div>
                    )}
                    {cats.map((c) => {
                        const style = CATEGORY_STYLE[c.key] || CATEGORY_STYLE.other;
                        const inAmt = c.in?.amount || 0;
                        const outAmt = c.out?.amount || 0;
                        const isOpen = expanded.has(c.key);
                        const allTypes = [
                            ...(c.in?.types || []).map((t) => ({ ...t, dir: 'in' })),
                            ...(c.out?.types || []).map((t) => ({ ...t, dir: 'out' })),
                        ];
                        return (
                            <div
                                key={c.key}
                                className={`rounded-lg border p-3 ${TONE_CLASSES[style.tone]}`}
                                data-testid={`ledger-cat-${c.key}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => toggle(c.key)}
                                    className="w-full flex items-center gap-3 text-left"
                                >
                                    {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                                    <div className="text-lg leading-none w-6 flex-shrink-0 text-center opacity-70">{style.emoji}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-black">{c.label}</div>
                                        <div className="text-[10px] opacity-60 truncate">{style.desc}</div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        {inAmt > 0 && (
                                            <div className="text-right">
                                                <div className="text-[9px] uppercase opacity-60">IN</div>
                                                <div className="text-[13px] font-mono font-black text-emerald-300 flex items-center gap-1">
                                                    <TrendingUp className="w-3 h-3" /> +{fmt(inAmt)}
                                                </div>
                                            </div>
                                        )}
                                        {outAmt > 0 && (
                                            <div className="text-right">
                                                <div className="text-[9px] uppercase opacity-60">OUT</div>
                                                <div className="text-[13px] font-mono font-black text-rose-300 flex items-center gap-1">
                                                    <TrendingDown className="w-3 h-3" /> -{fmt(outAmt)}
                                                </div>
                                            </div>
                                        )}
                                        <div className="text-right pl-2 border-l border-white/10">
                                            <div className="text-[9px] uppercase opacity-60">NET</div>
                                            <div className={`text-[13px] font-mono font-black ${c.net >= 0 ? 'text-emerald-200' : 'text-rose-200'}`} data-testid={`ledger-cat-${c.key}-net`}>
                                                {fmtSigned(c.net || 0)}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="mt-2 pl-11 pt-2 border-t border-white/10 space-y-2">
                                        {allTypes.length > 0 && (
                                            <div className="space-y-1">
                                                {allTypes.map((t) => (
                                                    <div
                                                        key={`${c.key}-${t.dir}-${t.type}`}
                                                        className="flex items-center justify-between text-[11px]"
                                                        data-testid={`ledger-type-${t.type}`}
                                                    >
                                                        <span className="font-mono opacity-70">{t.type}</span>
                                                        <span className={`font-mono font-black ${t.dir === 'in' ? 'text-emerald-300' : 'text-rose-300'}`}>
                                                            {t.dir === 'in' ? '+' : '-'}{fmt(t.amount)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Top-players drill-down */}
                                        <div className="pt-2 mt-2 border-t border-white/5">
                                            <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold mb-1">
                                                Top players in this bucket
                                            </div>
                                            {(() => {
                                                const cacheKey = `${c.key}|both`;
                                                const cached = topPlayersByCat[cacheKey];
                                                if (!cached || cached.loading) {
                                                    return (
                                                        <div className="text-[11px] opacity-60 flex items-center gap-1" data-testid={`ledger-top-players-${c.key}-loading`}>
                                                            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                                                        </div>
                                                    );
                                                }
                                                if (cached.rows.length === 0) {
                                                    return (
                                                        <div className="text-[11px] opacity-40" data-testid={`ledger-top-players-${c.key}-empty`}>
                                                            No player activity in this window.
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="space-y-0.5" data-testid={`ledger-top-players-${c.key}`}>
                                                        {cached.rows.map((p, i) => {
                                                            const signed = Number(p.signed || 0);
                                                            const isNet = signed !== 0;
                                                            return (
                                                                <div
                                                                    key={p.user_id}
                                                                    className="flex items-center justify-between text-[11px] py-0.5"
                                                                    data-testid={`ledger-top-player-${c.key}-${i}`}
                                                                >
                                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                        <span className="text-[9px] opacity-40 font-mono w-4 flex-shrink-0">#{i + 1}</span>
                                                                        <span className="font-mono truncate">
                                                                            {p.name}
                                                                            {p.phone && <span className="opacity-40 ml-1">· {p.phone}</span>}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                        <span className="text-[9px] opacity-50 font-mono">{p.count}×</span>
                                                                        <span className="text-[10px] opacity-60 font-mono">|{fmt(p.amount)}|</span>
                                                                        {isNet && (
                                                                            <span className={`font-mono font-black ${signed >= 0 ? 'text-emerald-300' : 'text-rose-300'} min-w-[68px] text-right`}>
                                                                                {fmtSigned(signed)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer — implied opening + delta explanation */}
                {data && (
                    <div className="px-5 py-3 border-t border-white/10 text-[10px] text-zinc-500 leading-relaxed rounded-b-2xl bg-zinc-950">
                        Window: <span className="text-zinc-300">{new Date(data.since).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</span>
                        {' → '}
                        <span className="text-zinc-300">{new Date(data.until).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</span>
                        {' · '}Scope: <span className="text-zinc-300">{data.scope}</span>
                        <br />
                        Implied opening float = current − net flow = <span className="font-mono text-zinc-300">{fmt(data.implied_opening_float || 0)}</span>
                        {'. '}Any difference vs your externally-known opening is drift from raw wallet writes that bypassed the transactions log.
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreditFlowLedgerModal;
